import { useCallback, useEffect, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"

import type { WorkflowItem } from "@/api/models"
import type { StartField } from "@/features/workflow-editor/utils/workflow-run-utils"

import { AlertCircle, Play } from "@/components/icons"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { FileUpload } from "@/features/ai-explorer/components/file-upload"
import {
  getWorkflowRunRequirements,
  resolveStartFields,
} from "@/features/workflow-editor/utils/workflow-run-utils"
import { cn } from "@/lib/utils"

/**
 * Collected per-field input values. Text fields map their key -> string; file
 * fields map their key -> File[]. The run trigger reads this as user_input_data.
 */
export type UserInputData = Record<string, string | File[]>

type WorkflowInputDialogProps = {
  workflow: WorkflowItem | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onRun: (data: {
    question: string
    files?: File[]
    /**
     * Per-field collected inputs, keyed by each start field's key. Optional so
     * existing callers that only consume { question, files } keep working.
     */
    user_input_data?: UserInputData
  }) => Promise<void>
}

function getStartNodeData(workflow: WorkflowItem | null): unknown {
  if (!workflow) return undefined
  try {
    const content = (
      typeof workflow.content === "string" ? JSON.parse(workflow.content) : workflow.content
    ) as { nodes?: Array<{ type?: string; data?: unknown }> }
    return content.nodes?.find((node) => node.type === "start")?.data
  } catch {
    return undefined
  }
}

export function WorkflowInputDialog({
  workflow,
  open,
  onOpenChange,
  onRun,
}: WorkflowInputDialogProps) {
  const { t } = useTranslation()

  // Aggregate requirements kept for legacy single-field labels / fallbacks and
  // for the `workflow.file_required` flag (which lives outside the start node).
  const { requiresInput, showFileUpload, filesRequired } = useMemo(
    () => getWorkflowRunRequirements(workflow),
    [workflow]
  )

  // Effective fields drive the entire form (new fields[] or legacy-derived).
  // If the workflow demands a file via the top-level `file_required` flag but the
  // start node declared none, append a synthetic legacy file field so the upload
  // still renders exactly as before (back-compat).
  const fields: StartField[] = useMemo(() => {
    const resolved = resolveStartFields(getStartNodeData(workflow) as never)
    if (showFileUpload && !resolved.some((field) => field.type === "file")) {
      // Derive a key that can't collide with an existing (possibly author-keyed)
      // field — a real field already keyed `file_<len>` would otherwise share both
      // the `fileValues` slot and the React subtree key with this synthetic one.
      const existingKeys = new Set(resolved.map((field) => field.key))
      let suffix = resolved.length
      while (existingKeys.has(`file_${suffix}`)) suffix += 1
      resolved.push({
        type: "file",
        label: "",
        required: filesRequired,
        key: `file_${suffix}`,
      })
    }
    return resolved
  }, [workflow, showFileUpload, filesRequired])

  // Per-field state, keyed by field.key.
  // Keyed by field.key; entries are absent until the user touches a field, so the
  // value types include undefined (justifies the optional access on read).
  const [textValues, setTextValues] = useState<Record<string, string | undefined>>({})
  const [fileValues, setFileValues] = useState<Record<string, File[] | undefined>>({})
  const [error, setError] = useState<string | null>(null)

  // Reset state whenever the dialog opens or the workflow changes.
  useEffect(() => {
    if (open) {
      setTextValues({})
      setFileValues({})
      setError(null)
    }
  }, [open, workflow])

  const handleTextChange = useCallback((key: string, value: string) => {
    setTextValues((prev) => ({ ...prev, [key]: value }))
  }, [])

  const handleAddFiles = useCallback((key: string, newFiles: File[]) => {
    setFileValues((prev) => ({ ...prev, [key]: [...(prev[key] ?? []), ...newFiles] }))
  }, [])

  const handleRemoveFile = useCallback((key: string, file: File) => {
    setFileValues((prev) => ({
      ...prev,
      [key]: (prev[key] ?? []).filter((f) => f !== file),
    }))
  }, [])

  const handleClose = useCallback(() => {
    onOpenChange(false)
  }, [onOpenChange])

  const handleSubmit = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault()
      if (!workflow) return

      // Validate required fields.
      for (const field of fields) {
        if (!field.required) continue
        if (field.type === "text" && !(textValues[field.key]?.trim() ?? "")) {
          setError(t("questioninput.workflow_selection.instructions_required"))
          return
        }
        if (field.type === "file" && (fileValues[field.key]?.length ?? 0) === 0) {
          setError(t("questioninput.workflow_selection.upload_documents_required"))
          return
        }
      }
      setError(null)

      // Build user_input_data keyed by each field's key.
      const userInputData: UserInputData = {}
      for (const field of fields) {
        if (field.type === "text") {
          userInputData[field.key] = textValues[field.key]?.trim() ?? ""
        } else {
          userInputData[field.key] = fileValues[field.key] ?? []
        }
      }

      // Back-compat single-field payload: first text field is the `question`,
      // all file fields flatten into `files`. When no fields are declared the
      // dialog shows a single free-form textarea under the `__legacy__` key.
      const firstTextField = fields.find((field) => field.type === "text")
      const questionRaw = firstTextField
        ? (textValues[firstTextField.key]?.trim() ?? "")
        : (textValues.__legacy__?.trim() ?? "")
      const question =
        questionRaw || `${t("questioninput.workflow_selection.running_workflow")} ${workflow.title}`
      const allFiles = fields
        .filter((field) => field.type === "file")
        .flatMap((field) => fileValues[field.key] ?? [])

      // Close dialog immediately - don't block the UI.
      onOpenChange(false)

      void onRun({
        question,
        files: allFiles.length > 0 ? allFiles : undefined,
        user_input_data: fields.length > 0 ? userInputData : undefined,
      }).catch((runError) => {
        // Error is logged but not shown to user since dialog is already closed.
        // eslint-disable-next-line no-console
        console.error("Failed to run workflow:", runError)
      })
    },
    [workflow, fields, textValues, fileValues, onRun, onOpenChange, t]
  )

  // Don't show dialog if no workflow.
  if (!workflow) {
    return null
  }

  const renderFieldLabel = (field: StartField, fallback: string) => (
    <Label className="text-sm font-medium">
      {field.label || fallback}
      {field.required && <span className="text-destructive ml-1">*</span>}
    </Label>
  )

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Play className="size-4" />
            {workflow.title}
          </DialogTitle>
          <DialogDescription>
            {t("questioninput.workflow_selection.provide_input", {
              defaultValue: "Provide input for this workflow",
            })}
          </DialogDescription>
        </DialogHeader>

        <form className="space-y-6" onSubmit={handleSubmit}>
          {fields.map((field, index) =>
            field.type === "text" ? (
              <div key={`${field.key}-${index}`} className="space-y-2">
                {renderFieldLabel(
                  field,
                  field.required
                    ? t("questioninput.workflow_selection.workflow_input_label_required")
                    : t("questioninput.workflow_selection.workflow_input_label")
                )}
                <Textarea
                  className={cn("min-h-[100px] resize-none", error && "border-destructive")}
                  placeholder={t("questioninput.workflow_selection.workflow_input_placeholder")}
                  value={textValues[field.key] ?? ""}
                  onChange={(event) => handleTextChange(field.key, event.target.value)}
                />
              </div>
            ) : (
              <div key={`${field.key}-${index}`} className="space-y-2">
                {renderFieldLabel(field, t("questioninput.workflow_selection.upload_documents"))}
                <FileUpload
                  files={fileValues[field.key] ?? []}
                  onAddFiles={(newFiles) => handleAddFiles(field.key, newFiles)}
                  onRemoveFile={(file) => handleRemoveFile(field.key, file)}
                />
              </div>
            )
          )}

          {/* Show a free-form instructions textarea whenever no explicit text field
              is declared, preserving the legacy always-present input box. */}
          {!fields.some((field) => field.type === "text") && (
            <div className="space-y-2">
              <Label className="text-sm font-medium">
                {requiresInput ? (
                  <span>
                    {t("questioninput.workflow_selection.workflow_input_label_required")}
                    <span className="text-destructive ml-1">*</span>
                  </span>
                ) : (
                  t("questioninput.workflow_selection.workflow_input_label")
                )}
              </Label>
              <Textarea
                className="min-h-[100px] resize-none"
                placeholder={t("questioninput.workflow_selection.workflow_input_placeholder")}
                value={textValues.__legacy__ ?? ""}
                onChange={(event) => handleTextChange("__legacy__", event.target.value)}
              />
            </div>
          )}

          {error && (
            <div className="text-destructive flex items-center gap-2 text-sm">
              <AlertCircle className="h-4 w-4" />
              {error}
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={handleClose}>
              {t("common.cancel")}
            </Button>
            <Button type="submit">
              <Play className="mr-2 size-4" />
              {t("questioninput.workflow_selection.run")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
